import { useEffect, useRef, useState } from "react";
import type { ClipboardEvent, KeyboardEvent } from "react";
import { ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { getErrorMessage } from "@/utils/getErrorMessage";
import { formatTime } from "@/utils";

interface OtpResponse {
  resendCooldownSeconds?: number;
  message?: string;
}

interface OtpInputProps {
  length?: number;
  cooldown?: number;

  /**
   * Called when the user requests the OTP for the first time.
   */
  onRequestOtp: () => Promise<OtpResponse>;

  /**
   * Optional separate resend function.
   * If omitted, onRequestOtp will be used for resend too.
   */
  onResendOtp?: () => Promise<OtpResponse>;

  /**
   * Called when the OTP is submitted.
   */
  onVerifyOtp: (otp: string) => Promise<void>;

  /**
   * Automatically request OTP when the component mounts.
   */
  autoRequest?: boolean;

  requestLabel?: string;
  verifyLabel?: string;
  resendLabel?: string;

  /**
   * Disables the entire OTP component.
   */
  disabled?: boolean;

  /**
   * Additional condition for disabling only
   * the Verify OTP button.
   *
   * Useful when another field such as a security
   * code must also be completed.
   */
  verifyDisabled?: boolean;
}

export function OtpInput({
  length = 6,
  cooldown = 60,
  onRequestOtp,
  onResendOtp,
  onVerifyOtp,
  autoRequest = false,
  requestLabel = "Send OTP",
  verifyLabel = "Verify OTP",
  resendLabel = "Resend OTP",
  disabled = false,
  verifyDisabled = false,
}: OtpInputProps) {
  const [otp, setOtp] = useState<string[]>(() => Array(length).fill(""));

  const [timer, setTimer] = useState(0);

  const [requesting, setRequesting] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);

  const [hasRequested, setHasRequested] = useState(false);

  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const otpString = otp.join("");

  const isComplete = otpString.length === length;

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, []);

  const startTimer = (seconds: number = cooldown) => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    setTimer(Math.max(0, seconds));

    if (seconds <= 0) {
      return;
    }

    timerRef.current = setInterval(() => {
      setTimer((current) => {
        if (current <= 1) {
          if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
          }

          return 0;
        }

        return current - 1;
      });
    }, 1000);
  };
  const clearOtp = () => {
    setOtp(Array(length).fill(""));
  };

  const focusFirstInput = () => {
    setTimeout(() => {
      inputRefs.current[0]?.focus();
    }, 100);
  };

  const handleRequestOtp = async () => {
    if (requesting || disabled) {
      return;
    }

    try {
      setRequesting(true);

      const response = await onRequestOtp();

      const cooldownSeconds = response?.resendCooldownSeconds ?? cooldown;

      clearOtp();

      setHasRequested(true);

      startTimer(cooldownSeconds);

      toast.success(response?.message || "OTP sent successfully.");

      focusFirstInput();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setRequesting(false);
    }
  };

  useEffect(() => {
    if (!autoRequest) {
      return;
    }

    void handleRequestOtp();

    // Intentionally only when component mounts.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleResendOtp = async () => {
    if (timer > 0 || resending || requesting || disabled) {
      return;
    }

    try {
      setResending(true);

      const requestFunction = onResendOtp ?? onRequestOtp;

      const response = await requestFunction();

      const cooldownSeconds = response?.resendCooldownSeconds ?? cooldown;

      clearOtp();

      setHasRequested(true);

      startTimer(cooldownSeconds);

      toast.success(response?.message || "OTP resent successfully.");

      focusFirstInput();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setResending(false);
    }
  };

  const handleOtpChange = (index: number, value: string) => {
    if (!/^\d?$/.test(value)) {
      return;
    }

    const nextOtp = [...otp];

    nextOtp[index] = value;

    setOtp(nextOtp);

    if (value && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (
    index: number,
    event: KeyboardEvent<HTMLInputElement>,
  ) => {
    if (event.key === "Backspace") {
      const nextOtp = [...otp];

      if (!otp[index] && index > 0) {
        nextOtp[index - 1] = "";

        setOtp(nextOtp);

        inputRefs.current[index - 1]?.focus();

        return;
      }

      nextOtp[index] = "";

      setOtp(nextOtp);

      return;
    }

    if (event.key === "ArrowLeft" && index > 0) {
      inputRefs.current[index - 1]?.focus();

      return;
    }

    if (event.key === "ArrowRight" && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpPaste = (event: ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();

    const pasted = event.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, length);

    if (!pasted) {
      return;
    }

    const nextOtp = Array(length)
      .fill("")
      .map((_, index) => pasted[index] ?? "");

    setOtp(nextOtp);

    const focusIndex = Math.min(pasted.length, length) - 1;

    if (focusIndex >= 0) {
      inputRefs.current[focusIndex]?.focus();
    }
  };

  const handleVerifyOtp = async () => {
    if (!isComplete || verifying || disabled || verifyDisabled) {
      return;
    }

    try {
      setVerifying(true);

      await onVerifyOtp(otpString);

      /**
       * Parent decides what happens after successful
       * verification.
       */
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* OTP INPUTS */}

      <div className="flex justify-center gap-2">
        {otp.map((digit, index) => (
          <Input
            key={index}
            ref={(element) => {
              inputRefs.current[index] = element;
            }}
            type="text"
            inputMode="numeric"
            autoComplete={index === 0 ? "one-time-code" : "off"}
            maxLength={1}
            value={digit}
            disabled={disabled || requesting}
            onChange={(event) => handleOtpChange(index, event.target.value)}
            onKeyDown={(event) => handleOtpKeyDown(index, event)}
            onPaste={handleOtpPaste}
            className="w-12 h-14 text-center text-xl font-bold"
          />
        ))}
      </div>

      {/* VERIFY */}

      <Button
        type="button"
        onClick={handleVerifyOtp}
        disabled={
          !isComplete ||
          verifying ||
          requesting ||
          resending ||
          disabled ||
          verifyDisabled
        }
        className="w-full h-14 rounded-xl font-semibold"
      >
        {verifying ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Verifying...
          </>
        ) : (
          <>
            {verifyLabel}
            <ArrowRight className="ml-2 h-4 w-4" />
          </>
        )}
      </Button>

      {/* REQUEST / RESEND */}

      {timer > 0 ? (
        <p className="text-center text-sm text-muted-foreground">
          Resend code in {formatTime(timer)}
        </p>
      ) : (
        <Button
          type="button"
          variant="ghost"
          onClick={hasRequested ? handleResendOtp : handleRequestOtp}
          disabled={requesting || resending || verifying || disabled}
          className="w-full"
        >
          {requesting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Sending...
            </>
          ) : resending ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Resending...
            </>
          ) : hasRequested ? (
            resendLabel
          ) : (
            requestLabel
          )}
        </Button>
      )}
    </div>
  );
}
